(() => {
  if (document.body.dataset.hnFloatingPreviewReady) return;
  document.body.dataset.hnFloatingPreviewReady = 'true';

  const mobile = matchMedia('(max-width: 749px)');
  let root;
  let canvas;
  let stage;
  let zoomIn;
  let zoomOut;
  let scale = 1;
  let panX = 0;
  let panY = 0;
  let drag;
  let dismissed = false;
  let shown = false;
  let hasFrame = false;
  let selectedByUser = false;
  let revealPending = false;
  const positions = new Map();
  const targets = new Map();
  const visibility = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      for (const [key, target] of targets) {
        if (target === entry.target) positions.set(key, entry);
      }
    }
    sync();
  });
  const watchVisibility = () => {
    const options = document.querySelector('.customily_sets-panel');
    const next = {
      gallery: document.querySelector('.product-information__media'),
      options,
      purchase: document.querySelector('.product-form-buttons') || options,
      modal: document.querySelector('.customily-modal-preview-only'),
    };
    for (const [key, target] of Object.entries(next)) {
      if (targets.get(key) === target) continue;
      const previous = targets.get(key);
      targets.set(key, target);
      if (previous && ![...targets.values()].includes(previous)) visibility.unobserve(previous);
      positions.delete(key);
      if (target) visibility.observe(target);
    }
  };

  const hasSelectedOption = (options) => {
    // The HNG ornament's first required choice is the quote. Customily can
    // render its canvas before this input has a value.
    const quote = window.HanossCustomilyCapabilities?.getConfiguredFields?.().quote
      || options.querySelector('input[name="properties[Choose Quote]"]');
    if (quote) return Boolean(quote.value.trim());
    return [...options.querySelectorAll('[name^="properties["]')].some((input) =>
      input.type === 'radio' || input.type === 'checkbox' ? input.checked : Boolean(input.value?.trim()));
  };

  const sourceCanvas = () => {
    // Crossword surfaces share the accepted snapshot. A sized provider canvas
    // can contain an old design while the shopper's latest edit is generating.
    if (window.HanossCustomilyCapabilities?.getConfiguredFields?.().crosswordOption) {
      if (!document.body.classList.contains('hn-ui-render-current')
        && !document.body.classList.contains('hn-ui-quote-gallery')) return null;
      const accepted = document.querySelector('.product-information__media .hn-ui-live-canvas[data-rendered="true"]');
      return accepted?.width && accepted?.height ? accepted : null;
    }
    const native = document.querySelector('.customily-modal-preview-only #preview-canvas');
    if (native?.width && native?.height) return native;
    const live = document.querySelector('.product-information__media .hn-ui-live-canvas[data-rendered="true"]');
    if (live?.width && live?.height) return live;
    return null;
  };

  const applyZoom = () => {
    const horizontalLimit = Math.max(0, (canvas.clientWidth * scale - stage.clientWidth) / 2);
    const verticalLimit = Math.max(0, (canvas.clientHeight * scale - stage.clientHeight) / 2);
    panX = Math.max(-horizontalLimit, Math.min(horizontalLimit, panX));
    panY = Math.max(-verticalLimit, Math.min(verticalLimit, panY));
    canvas.style.setProperty('--hn-preview-scale', String(scale));
    canvas.style.setProperty('--hn-preview-pan-x', `${panX}px`);
    canvas.style.setProperty('--hn-preview-pan-y', `${panY}px`);
    zoomIn.disabled = scale >= 2;
    zoomOut.disabled = scale <= 1;
  };

  const sync = () => {
    watchVisibility();
    const gallery = document.querySelector('.product-information__media');
    const options = document.querySelector('.customily_sets-panel');
    if (!root || !gallery || !options || !mobile.matches) {
      root?.classList.remove('is-visible');
      shown = false;
      return;
    }
    const galleryBottom = positions.get('gallery')?.boundingClientRect.bottom;
    if (galleryBottom > 0) dismissed = false;
    const optionsTop = positions.get('options')?.boundingClientRect.top;
    const purchaseBottom = positions.get('purchase')?.boundingClientRect.bottom;
    const nativeOpen = positions.get('modal')?.isIntersecting;
    const source = sourceCanvas();
    const updating = document.body.classList.contains('hn-ui-gallery-loading')
      || Boolean(window.HanossCustomilyCapabilities?.getConfiguredFields?.().crosswordOption && !source);
    const wasLoading = root.classList.contains('is-loading');
    // Keep only an already visible, painted preview through an option update.
    // Scroll/dismiss/modal/header gates still hide it immediately.
    const keepWhileUpdating = shown && hasFrame && updating;
    const visible = !dismissed && galleryBottom < 0 && optionsTop < innerHeight && purchaseBottom > 0
      && !nativeOpen && !document.body.classList.contains('hn-ui-cart-editor')
      && !document.querySelector('.hn-header-search.is-active, #Details-menu-drawer-container[open]')
      && selectedByUser && hasSelectedOption(options)
      && ((!updating && Boolean(source)) || keepWhileUpdating || (updating && revealPending));
    if (visible && !updating && source) revealPending = false;
    root.classList.toggle('is-visible', visible);
    root.classList.toggle('is-loading', visible && updating);
    root.setAttribute('aria-busy', String(visible && updating));
    root.setAttribute('aria-hidden', String(!visible));
    const appeared = visible && !shown;
    shown = visible;
    if (appeared || (visible && wasLoading && !updating)) window.HanossCustomilyPreviewFrames?.requestFrame();
  };

  const paint = () => {
    if (!mobile.matches || document.hidden) return;
    if (!shown || root.classList.contains('is-loading')) return;
    const source = sourceCanvas();
    if (!source) return;
    if (canvas.width !== source.width || canvas.height !== source.height) {
      canvas.width = source.width;
      canvas.height = source.height;
    }
    try { canvas.getContext('2d').drawImage(source, 0, 0); hasFrame = true; } catch { /* Customily is replacing the frame. */ }
  };

  const create = () => {
    if (!mobile.matches || root || !document.querySelector('.customily_sets-panel')) return;
    root = document.createElement('div');
    root.className = 'hn-mobile-floating-preview';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', 'Live personalized design preview');
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML = '<div class="hn-mobile-floating-preview__stage"><canvas class="hn-mobile-floating-preview__canvas" aria-label="Live personalized design"></canvas></div><button class="hn-mobile-floating-preview__close" type="button" aria-label="Close floating preview"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg></button><div class="hn-mobile-floating-preview__zoom"><button type="button" data-zoom="in" aria-label="Zoom in"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="7"/><path d="m16 16 5 5M10.5 7v7M7 10.5h7"/></svg></button><button type="button" data-zoom="out" aria-label="Zoom out" disabled><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="7"/><path d="m16 16 5 5M7 10.5h7"/></svg></button></div>';
    document.body.append(root);
    canvas = root.querySelector('canvas');
    stage = root.querySelector('.hn-mobile-floating-preview__stage');
    zoomIn = root.querySelector('[data-zoom="in"]');
    zoomOut = root.querySelector('[data-zoom="out"]');
    root.querySelector('.hn-mobile-floating-preview__close').addEventListener('click', () => {
      dismissed = true;
      revealPending = false;
      sync();
    });
    root.querySelectorAll('[data-zoom]').forEach((button) => button.addEventListener('click', () => {
      scale = Math.max(1, Math.min(2, scale + (button.dataset.zoom === 'in' ? .5 : -.5)));
      if (scale === 1) panX = panY = 0;
      applyZoom();
    }));
    stage.addEventListener('pointerdown', (event) => {
      if (scale === 1) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, panX, panY };
      stage.setPointerCapture(event.pointerId);
    });
    stage.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      panX = drag.panX + event.clientX - drag.x;
      panY = drag.panY + event.clientY - drag.y;
      applyZoom();
    });
    stage.addEventListener('pointerup', () => { drag = null; });
    stage.addEventListener('pointercancel', () => { drag = null; });
    addEventListener('resize', sync, { passive: true });
    addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && shown) { dismissed = true; sync(); }
    });
    window.HanossCustomilyPreviewFrames?.register(stage, paint, { priority: 1 });
    // Modal/loading/header visibility used to be refreshed by the paint timer.
    // Observe only those state owners when polling is removed.
    let syncFrame;
    const scheduleSync = () => {
      if (syncFrame) return;
      syncFrame = requestAnimationFrame(() => { syncFrame = null; sync(); });
    };
    const stateObserver = new MutationObserver(scheduleSync);
    stateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    for (const owner of document.querySelectorAll('.customily-modal-preview-only, .hn-header-search, #Details-menu-drawer-container')) {
      stateObserver.observe(owner, { attributes: true, attributeFilter: ['class', 'style', 'open'] });
    }
    const onOptionInput = (event) => {
      if (event.isTrusted && event.target?.closest?.('.customily_sets-panel')) {
        selectedByUser = true;
        const fields = window.HanossCustomilyCapabilities?.getConfiguredFields?.();
        const quote = fields?.quote || document.querySelector('input[name="properties[Choose Quote]"]');
        if (event.target === quote || fields?.quoteOption?.contains(event.target)) {
          revealPending = hasSelectedOption(document.querySelector('.customily_sets-panel'));
        }
      }
      sync();
    };
    document.addEventListener('change', onOptionInput, true);
    document.addEventListener('input', onOptionInput, true);
    document.addEventListener('click', (event) => {
      if (!event.target?.closest?.('.customily_sets-panel')) return;
      if (event.isTrusted && event.target.closest('.csh-dropdown-item, .customily_option label')) {
        selectedByUser = true;
        const quoteOption = window.HanossCustomilyCapabilities?.getConfiguredFields?.().quoteOption;
        if (quoteOption?.contains(event.target)) revealPending = true;
      }
      setTimeout(sync, 0);
    }, true);
    sync();
  };

  let panelSubscription;
  const start = () => {
    if (!panelSubscription && window.HanossCustomilyCapabilities?.onPanelChange) {
      panelSubscription = window.HanossCustomilyCapabilities.onPanelChange(() => { create(); sync(); });
    }
    if (mobile.matches) create();
    sync();
  };
  mobile.addEventListener('change', start);
  document.addEventListener('hanoss:customily-panel-ready', start);
  document.addEventListener('shopify:section:load', start);
  start();
})();
